import React from "react";

import DrawingEngine from "../drawingEngine";

// Binding hook for DrawingEngine. The engine owns strokes / redoStack /
// active stroke / canvas painting / flood fill; this hook just pushes
// React-side inputs in (setInputs) and snapshots engine output back into
// React state for downstream consumers (DrawingToolbar's enable state,
// scoring's strokes input, the speedrun activity signals).
//
// Returns the same shape the previous useDrawing did so callers don't
// need to change: { canvasHandlers, undo, redo, strokes, canUndo,
// canRedo, lastTool, lastActionAt }.
export default function useDrawing({
  canvasRef,
  tool,
  palette,
  colorIndex,
  size,
  eggSilhouetteRef,
  freezeStrokeColor = false,
}) {
  const engineRef = React.useRef(null);
  if (engineRef.current == null) {
    engineRef.current = new DrawingEngine({ canvasRef, eggSilhouetteRef });
  }

  React.useEffect(() => {
    engineRef.current.setInputs({
      tool,
      palette,
      colorIndex,
      size,
      freezeStrokeColor,
    });
  }, [tool, palette, colorIndex, size, freezeStrokeColor]);

  const [snapshot, setSnapshot] = React.useState(() =>
    engineRef.current.getSnapshot(),
  );

  const recompute = React.useCallback(() => {
    setSnapshot(engineRef.current.getSnapshot());
  }, []);

  React.useEffect(() => {
    const engine = engineRef.current;
    const unsub = engine.subscribe(recompute);
    return () => {
      unsub();
      engine.dispose();
    };
  }, [recompute]);

  // Stable handlers — delegate to the engine. Methods on the class read
  // .canvasRef.current internally, so the closure doesn't need to
  // capture the canvas.
  const canvasHandlers = React.useMemo(
    () => ({
      onPointerDown: (e) => engineRef.current.onPointerDown(e),
      onPointerMove: (e) => engineRef.current.onPointerMove(e),
      onPointerUp: (e) => engineRef.current.onPointerUp(e),
      onPointerCancel: (e) => engineRef.current.onPointerUp(e),
    }),
    [],
  );

  const undo = React.useCallback(() => engineRef.current.undo(), []);
  const redo = React.useCallback(() => engineRef.current.redo(), []);
  const clear = React.useCallback(() => engineRef.current.clear(), []);

  return {
    canvasHandlers,
    undo,
    redo,
    clear,
    strokes: snapshot.strokes,
    canUndo: snapshot.canUndo,
    canRedo: snapshot.canRedo,
    lastTool: snapshot.lastTool,
    lastActionAt: snapshot.lastActionAt,
  };
}
